import React from "react";
import {
  type StyleProp,
  StyleSheet,
  Text,
  View,
  type ViewStyle,
} from "react-native";
import { MaterialIcons } from "@expo/vector-icons";
import Animated, { FadeIn } from "react-native-reanimated";

type NoticeTimestamp = {
  lastUpdate: string;
  nextUpdate?: string;
};

type NoticeVariant = "info" | "warning" | "error" | "success";

type NoticeProps = {
  icon?: keyof typeof MaterialIcons.glyphMap;
  title: string;
  message: string;
  timestamp: NoticeTimestamp;
  variant?: NoticeVariant;
  style?: StyleProp<ViewStyle>;
};

const getVariantColors = (
  variant: NoticeVariant = "info",
): { primary: string; secondary: string } => {
  const colors: Record<NoticeVariant, { primary: string; secondary: string }> =
    {
      info: {
        primary: "#01579b",
        secondary: "#6B7280",
      },
      warning: {
        primary: "#d97706",
        secondary: "#f59e0b",
      },
      error: {
        primary: "#dc2626",
        secondary: "#ef4444",
      },
      success: {
        primary: "#16a34a",
        secondary: "#22c55e",
      },
    };
  return colors[variant];
};

const Notice: React.FC<NoticeProps> = ({
  icon = "notifications",
  title,
  message,
  timestamp,
  variant = "info",
  style,
}) => {
  const colors = getVariantColors(variant);

  return (
    <Animated.View
      entering={FadeIn.duration(200)}
      style={[
        styles.container,
        { backgroundColor: `${colors.primary}10` },
        style,
      ]}
    >
      <View style={styles.contentContainer}>
        <MaterialIcons
          name={icon}
          size={24}
          color={colors.primary}
          style={styles.icon}
        />

        <View style={styles.textContainer}>
          <Text style={[styles.title, { color: colors.primary }]}>{title}</Text>
          <Text style={[styles.message, { color: colors.secondary }]}>
            {message}
          </Text>

          <View style={styles.timestampContainer}>
            <Text style={[styles.timestamp, { color: colors.secondary }]}>
              Last update: {timestamp.lastUpdate}
            </Text>
          </View>
        </View>
      </View>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginHorizontal: 16,
    marginVertical: 8,
    borderRadius: 12,
    overflow: "hidden",
  },
  contentContainer: {
    flexDirection: "row",
    alignItems: "flex-start",
    padding: 12,
  },
  icon: {
    marginTop: 2,
  },
  textContainer: {
    flex: 1,
    marginLeft: 12,
  },
  title: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 4,
  },
  message: {
    fontSize: 14,
    lineHeight: 20,
  },
  timestampContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    marginTop: 8,
  },
  timestamp: {
    fontSize: 12,
  },
});

export default Notice;
